import { CancelTokenSource } from "axios";
import { Grid } from "../core/components/grid.js";
import { Config } from "../core/config/config.js";
import { Helper } from "../core/util/helper.js";
import { FAIcon } from "../core/components/faIcon.js";
import { GridCustomToolBarButton } from "../core/components/toolBar.js";
import { ArticleFilter, StocksContext, Warehouse } from "./stocks.js";
import { General } from "../../siteManagement/general.js";
import { SheetEventType } from "../../siteManagement/sheet.js";
import { MoveDialogProps, MoveDialog, MoveDialogResult } from "./moveDialog.js";
import { HTTPService, JsonResultClass } from "../core/util/httpManager.js";
import { OpenUsagesGrid } from "./usagesGrid.js";
import { OpenHistoryGrid } from "./historyGrid.js";
import React from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { NotificationHelper } from "../core/util/notificationHelper.js";
export interface StocksGridProps {
    selectedWarehouse: number;
    context: StocksContext;
    articleFilter: ArticleFilter;
    afterTransfer: () => void;
}
interface StocksGridState {
    data: Stock[];
    isLoading: boolean
}
export interface Stock {
    StorageSpaceId: number;
    State: number;

    ArtNr: number;
    ChargenNr: string;
    SerienNr: string;
    Article: string;
    ArticleNumber: string;
    Amount: number;
}
export class StocksGrid extends React.Component<StocksGridProps, StocksGridState> {
    private lastCancelToken: CancelTokenSource;
    private gridOptions: kendo.ui.GridOptions;
    private OK_STATUS = 200;
    private TRANSFER_BTN = "transfer";
    private USAGES_BTN = "usages";
    private HISTORY_BTN = "history";
    private gridRef: kendo.ui.Grid;

    constructor(props: StocksGridProps) {
        super(props)
        this.state = {
            data: [],
            isLoading: false
        }
        this.initApp();
    }

    private initApp = () => {
        const getSpaceStorage = (dataItem: Stock) => {
            return this.props.context.storaceSpaces[dataItem.StorageSpaceId];
        }

        const getState = (dataItem: Stock) => {
            const item = this.props.context.stockTypes.find(f => f.Key == dataItem.State);
            if (item) {
                return item.Value;
            }
            return dataItem.State ?? "";
        }

        const compareFnc = (fnc: (dataItem: Stock) => string | number) => {
            return (a: Stock, b: Stock) => {
                const name_a = fnc(a);
                const name_b = fnc(b);
                if (name_a < name_b) {
                    return -1;
                } else if (name_a > name_b) {
                    return 1;
                }
                return 0;
            }
        }

        this.gridOptions = {
            sortable: true,
            pageable: false,
            groupable: false,
            selectable: true,
            resizable: true,
            scrollable: true,
            filterable: {
                extra: false
            },
            editable: false,
            columns: [
                {
                    field: "StorageSpaceId",
                    title: Helper.getTranslation("WebSMS.Warehouse.StorageSpace"),
                    template: getSpaceStorage,
                    filterable: {
                        ui: (element: any) => {
                            element.kendoDropDownList({
                                dataTextField: "Name",
                                dataValueField: "Id",
                                dataSource: this.props.context.warehouses.find(w => w.Id == this.props.selectedWarehouse).StorageSpaces,
                                optionLabel: Helper.getTranslation("Standard.BitteWählen")
                            });
                        }
                    },
                    sortable: {
                        compare: compareFnc(getSpaceStorage)
                    }
                },
                {
                    field: "Article",
                    title: Helper.getTranslation("BrowserTitle.Parts.Available")
                },
                {
                    field: "ArticleNumber",
                    title: Helper.getTranslation("WebSMS.PartsList.ArticleNo"),
                    template: (dataItem: Stock) => {
                        return `<a data-artnr="${dataItem.ArtNr}">${dataItem.ArticleNumber}</a>`
                    }
                },
                //{
                //    field: "ChargenNr",
                //    title: Helper.getTranslation("Field.Material.chargennr")
                //},
                //{
                //    field: "SerienNr",
                //    title: Helper.getTranslation("Field.Material.seriennr")
                //},
                {
                    field: "State",
                    title: Helper.getTranslation("Field.Adresse.status"),
                    template: getState,
                    sortable: {
                        compare: compareFnc(getState)
                    },
                    filterable: {
                        ui: (element: any) => {
                            element.kendoDropDownList({
                                dataTextField: "Value",
                                dataValueField: "Key",
                                dataSource: this.props.context.stockTypes,
                                optionLabel: Helper.getTranslation("Standard.BitteWählen")
                            });
                        }
                    }
                },
                {
                    field: "Amount",
                    title: Helper.getTranslation("WebSMS.PartsList.ArtAmount"),
                    filterable: {
                        ui: (element: any) => {
                            element.kendoNumericTextBox({
                                min: 0
                            });
                        }
                    }
                },
            ]
        }
    }

    private cancelRequest = () => {
        if (Helper.hasValue(this.lastCancelToken)) {
            this.lastCancelToken.cancel();
            this.lastCancelToken = null;
        }
    }

    public loadData = () => {
        return new Promise<void>((resolve) => {
            const id = this.props.selectedWarehouse;
            this.setState({ isLoading: true }, async () => {
                this.cancelRequest();
                const currentToken = axios.CancelToken.source();
                this.lastCancelToken = currentToken;

                const result = await axios.post<Stock[]>(Config.baseUrl + "Stock/GetStocksByWarehouseId?id=" + id, {
                    cancelToken: currentToken.token
                });
                if (result.status == this.OK_STATUS) {
                    let data = [];
                    if(Array.isArray(result.data)){
                        data = result.data.sort(this.compareStocks);
                    }
                    this.setState({ isLoading: false, data: data }, resolve)
                }
            })
        })
    }

    private compareStocks = (a: Stock, b: Stock) => {
        const storageA = this.props.context.storaceSpaces[a.StorageSpaceId];
        const storageB = this.props.context.storaceSpaces[b.StorageSpaceId];
        if (storageA < storageB) {
            return -1;
        } else if (storageA > storageB) {
            return 1;
        } else {
            if (a.Article < b.Article) {
                return -1;
            } else if (a.Article > b.Article) {
                return 1;
            }
        }

        return 0;
    }

    private onBtnClick = async (id: string, dataItem: Stock) => {
        let title = dataItem.Article;
        const warehouse = this.props.context.warehouses.find(w => w.Id == this.props.selectedWarehouse);
        if (warehouse) {
            const storageSpace = warehouse.StorageSpaces.find(s => s.Id == dataItem.StorageSpaceId);
            if (storageSpace) {
                title += ` (${warehouse.Name}/${storageSpace.Name})`
            }
        }
        switch (id) {
            case this.TRANSFER_BTN:
                await this.transfer(dataItem);
                break;
            case this.USAGES_BTN:
                await OpenUsagesGrid(dataItem, title);
                break;
            case this.HISTORY_BTN:
                await OpenHistoryGrid(dataItem, this.props.context, title)
                break;
        }
    }

    private transfer = async (stock: Stock) => {
        const data = await this.openDialog(stock);
        const { Code, Message } = await HTTPService.post<JsonResultClass>("Stock/Transfer", {
            ArtNr: stock.ArtNr,
            ChargenNr: stock.ChargenNr,
            SerienNr: stock.SerienNr,
            SourceStorageSpace: stock.StorageSpaceId,
            TargetStorageSpace: data.storageSpace,
            Amount: data.amount
        });
        if (Code == 0) {
            this.props.afterTransfer();
        } else {
            NotificationHelper.showErrorMessage(Message)
        }
        await this.loadData();
    }

    private openDialog = (stock: Stock) => {
        return new Promise<MoveDialogResult>((resolve, reject) => {
            const targetElement = $("<div>");
            $("body").append(targetElement);
            const root = ReactDOM["createRoot"](targetElement[0]);
            const removeDialog = () => {
                root.unmount(targetElement[0]);
                targetElement[0].remove();
            }

            let warehouses = JSON.parse(JSON.stringify(this.props.context.warehouses));
            for (let i = 0; i < warehouses.length; i++) {
                warehouses[i].StorageSpaces = warehouses[i].StorageSpaces.filter(s => s.Id != stock.StorageSpaceId)
            }
            warehouses = warehouses.filter(w => w.StorageSpaces.length > 0);
            if (warehouses.length === 0) {
                const msg = "Kein Lagerplatz zum verschieben vorhanden";
                NotificationHelper.showInfoMessage(msg);
                reject(msg)
            } else {
                const props: MoveDialogProps = {
                    onCancel: () => {
                        removeDialog();
                    },
                    onOk: (result) => {
                        removeDialog();
                        resolve(result)
                    },
                    context: this.props.context,
                    warehouses: warehouses,
                    max: stock.Amount
                };

                root.render(<MoveDialog {...props} />);
            }


            
        });
    }

    private openArticleFnc = (artNr: number) => {
        return () => {
            const sheet = General.Sheet.Manager.createSheetWithUrl(`Parts/Edit?id=${artNr}`);
            sheet.bind(SheetEventType.Close, async () => {
                await this.loadData()
            });
        }
    }

    private onDataBound = (e) => {
        const data = e.sender.dataSource.view();
        for (let i = 0; i < data.length; i++) {
            const link = e.sender.tbody.find("tr[data-uid='" + data[i].uid + "'] a");
            if (link) {
                link.click(this.openArticleFnc(data[i].ArtNr))
            }
        }
    }

    async componentDidUpdate(prevProps: StocksGridProps) {
        if (this.props.selectedWarehouse != prevProps.selectedWarehouse) {
            await this.loadData()
        } else if(this.gridRef) {
            
            this.gridRef.dataSource.read();
        }
    }

    async componentDidMount() {
        await this.loadData()
    }

    private getData = () => {
        let data = this.state.data;
        if (this.props.articleFilter) {
            const { ArtNrs, ChargenNr, SerienNr } = this.props.articleFilter;
            if (ArtNrs.length > 0) {
                data = data.filter(f => ArtNrs.findIndex(a => a == f.ArtNr) > -1)
            }
            if (!Helper.isNullOrEmpty(ChargenNr)) {
                data = data.filter(f => f.ChargenNr.includes(ChargenNr))
            }
            if (!Helper.isNullOrEmpty(SerienNr)) {
                data = data.filter(f => f.ChargenNr.includes(SerienNr))
            }

        }
        return data;
    }

    render() {
        if (this.state.isLoading) {
            return (
                <div style={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FAIcon name="spinner" prefix={"fas"} spins={true} attributes={["spin", "10x"]} style={{ height: "fit-content", width: "fit-content", color: "#aeaeae" }} />
                </div>
            )
        } else if (Helper.hasValue(this.props.selectedWarehouse)) {
            const customButtons: Array<GridCustomToolBarButton> = [
                {
                    autoEnabledOnRecordSelected: true,
                    caption: Helper.getTranslation("WebSMS.Warehouse.IncomingGoods.Verschieben"),
                    enabled: true,
                    iconClass: "fa-light fa-right-left-large",
                    id: this.TRANSFER_BTN
                },
                {
                    autoEnabledOnRecordSelected: true,
                    caption: Helper.getTranslation("WebSMS.Reminder.History"),
                    enabled: true,
                    iconClass: "fa-light fa-clock",
                    id: this.HISTORY_BTN
                },
                {
                    autoEnabledOnRecordSelected: true,
                    caption: Helper.getTranslation("WebSMS.Warehouse.Usage"),
                    enabled: true,
                    iconClass: "fa-light fa-box-open",
                    id: this.USAGES_BTN
                }
            ];
            return (
                <div className="flex-container">
                    <Grid
                        data={this.getData()}
                        options={this.gridOptions}
                        id={`stocks`}
                        customButtons={customButtons}
                        onClickCustomButton={this.onBtnClick}
                        ref={(ref) => {
                            if (ref)
                                this.gridRef = ref.getGridRef()
                        }}
                        onDataBound={this.onDataBound}
                    />
                </div>
            )
        } else {
            return null;
        }
    }
}